import { Intent } from '@blueprintjs/core';
import * as React from 'react';
import { useHistory } from 'react-router-dom';

import { withConfirmation } from 'src/components/withConfirmation';
import { ProjectDiagramTileId } from 'src/constants/mosaic';
import { PROJECT_DIAGRAM_RESCALE_PARAM } from 'src/constants/project';
import { ProjectDiagramModelList } from 'src/containers/ProjectDiagram/components/modelList/ProjectDiagramModelList';
import { useContextMenu } from 'src/containers/ProjectDiagram/hooks/useContextMenuValue';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { useIsLargeModel } from 'src/containers/ProjectDiagram/hooks/useIsLargeModel';
import { ProjectDiagramContainer } from 'src/containers/ProjectDiagram/ProjectDiagramContainer';
import { redrawLayout } from 'src/containers/ProjectDiagram/templates/extensions/layouts/utils/redrawLayout';
import { commitDiagramWhileSkippingUndoManager } from 'src/containers/ProjectDiagram/utils/commitDiagramModel';
import { ProjectAutoGenerateRunner } from 'src/containers/ProjectResourceSuggestionsDialog/ProjectAutoGenerateRunner';
import { pendingAutoGenerateMockDataSelector } from 'src/selectors/pendingAutoGenerateMockData';
import { pendingAutoGenerateResourceIdsSelector } from 'src/selectors/pendingAutoGenerateModel';
import { uiSlice } from 'src/store/ui/ui.slice';
import * as copy from 'src/templates/copy';
import { usePrevious } from 'src/utils/hooks/usePrevious';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

export const ProjectDiagramView = () => {
  const { projectId } = useRouterParams();
  const dispatch = useReduxDispatch();
  const [contextMenu, setContextMenu] = useContextMenu();
  const diagram = useDiagram();
  const history = useHistory();
  const isModelListOpen = useReduxSelector(
    (state) => state.ui.modelList.isOpen
  );
  const forceDiagram = useReduxSelector(
    (state) => state.ui.modelList.forceDiagram
  );
  const isLargeModel = useIsLargeModel();
  const wasLargeModel = usePrevious(isLargeModel);

  // Large models default to the list view (no diagram is built) unless the user
  // opts into the diagram. Render the list on the derived condition so there's
  // no empty-canvas flash before the sync effect below runs
  const showList = isModelListOpen || (isLargeModel && !forceDiagram);

  // Keep `isModelListOpen` in sync so the toolbar's diagram-only controls stay
  // disabled while a large model is showing the list by default. Only fires on
  // entry — once the user opts into the diagram, forceDiagram is true.
  React.useEffect(() => {
    if (isLargeModel && !forceDiagram && !isModelListOpen) {
      dispatch(uiSlice.actions.toggledModelList({ isOpen: true, projectId }));
    }
  }, [dispatch, forceDiagram, isLargeModel, isModelListOpen, projectId]);

  // Once a real graph is on screen, mark it the active surface (forceDiagram).
  // This means a model that grows past the large threshold mid-edit is NOT torn
  // down — `headless` stays false — so the user keeps working instead of being
  // snapped to the list
  React.useEffect(() => {
    if (diagram && !diagram.headless && !isModelListOpen && !forceDiagram) {
      dispatch(
        uiSlice.actions.toggledModelList({
          forceDiagram: true,
          isOpen: false,
          projectId,
        })
      );
    }
  }, [diagram, dispatch, forceDiagram, isModelListOpen, projectId]);

  // The model crossed the large threshold while the user was working in the
  // graph (forceDiagram is only set once a graph has been shown, so this can't
  // fire on a fresh large-model open). Keep them in the graph but suggest the
  // more responsive list view
  React.useEffect(() => {
    if (
      wasLargeModel === false &&
      isLargeModel &&
      forceDiagram &&
      !isModelListOpen
    ) {
      const { largeModelReachedConfirmation } =
        copy.components.projectDiagram.navbar;
      withConfirmation({
        message: largeModelReachedConfirmation.message,
        noPrompt: largeModelReachedConfirmation.cancel,
        onConfirmationSuccess: () =>
          dispatch(
            uiSlice.actions.toggledModelList({
              forceDiagram: false,
              isOpen: true,
              projectId,
            })
          ),
        title: largeModelReachedConfirmation.title,
        yesButtonIntent: Intent.SUCCESS,
        yesPrompt: largeModelReachedConfirmation.confirm,
      });
    }
  }, [
    dispatch,
    forceDiagram,
    isLargeModel,
    isModelListOpen,
    projectId,
    wasLargeModel,
  ]);

  // Handle auto-model generation from create project flow
  const hasPendingAutoGenerate = useReduxSelector((state) =>
    Boolean(
      pendingAutoGenerateResourceIdsSelector(projectId)(state)?.length ||
        pendingAutoGenerateMockDataSelector(projectId)(state)
    )
  );

  React.useEffect(() => {
    const { search, pathname } = history.location;
    const params = new URLSearchParams(search);

    if (
      params.has(PROJECT_DIAGRAM_RESCALE_PARAM) &&
      diagram &&
      !diagram.headless
    ) {
      history.replace(pathname);
      commitDiagramWhileSkippingUndoManager(diagram, redrawLayout);
    }
  }, [diagram, history]);

  return (
    <div className="sd-project-diagram-view">
      <ProjectDiagramContainer
        contextMenu={contextMenu}
        id={ProjectDiagramTileId.VIEW}
        onContextMenuClose={() => {
          setContextMenu(null);
          // return focus to gojs
          if (diagram && !diagram.headless) {
            diagram.toolManager.contextMenuTool.doCancel();
          }
        }}
      />
      {showList ? <ProjectDiagramModelList /> : null}
      {hasPendingAutoGenerate ? <ProjectAutoGenerateRunner /> : null}
    </div>
  );
};
